import React from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { Typography, TypographyVariant } from 'components/core';
import CustomSwitch from 'components/core/forms/custom-switch/custom-switch';
import {
  useApprovalPolicy,
  useUpdateApprovalPolicy,
} from './hooks/use-approval-policy';
import { Styled } from './dual-approval-toggle.style';

const ENABLE_MULTIPLE_APPROVAL = 'ENABLE_MULTIPLE_APPROVAL';
const DISABLE_MULTIPLE_APPROVAL = 'DISABLE_MULTIPLE_APPROVAL';

const WAITING_LABEL = 'Waiting for a second admin';
const INSUFFICIENT_ADMINS_TOOLTIP =
  'There must be at least two admin business members to enable dual approval.';
const DUAL_APPROVAL_SUMMARY =
  'A second admin must approve withdrawals, allocation changes, and new admins.';
const RECOVERY_TOOLTIP =
  'If enabled and all other admins lost account access, please email support@treasure.tech for recovery assistance.';
const RECOVERY_LABEL =
  'All other admins lost account access while dual-approval is enabled? Please email support@treasure.tech for recovery assistance.';

const tooltipTitle = (message: string) => (
  <Styled.TooltipBody bgcolor="white" p={2} width={265}>
    <Typography variant={TypographyVariant.Paragraph2}>{message}</Typography>
  </Styled.TooltipBody>
);

export const DualApprovalToggle: React.FC = () => {
  const { data: policy, isLoading } = useApprovalPolicy();
  const updateApprovalPolicy = useUpdateApprovalPolicy();

  if (isLoading || !policy) {
    return null;
  }

  const isPending =
    policy.pendingRequestType === ENABLE_MULTIPLE_APPROVAL ||
    policy.pendingRequestType === DISABLE_MULTIPLE_APPROVAL;

  const requestedWhileSaving =
    !isPending && updateApprovalPolicy.isLoading
      ? updateApprovalPolicy.variables?.enabled
      : undefined;

  let checked = policy.enabled;

  if (requestedWhileSaving !== undefined) {
    checked = requestedWhileSaving;
  } else if (isPending) {
    checked = policy.pendingRequestType === ENABLE_MULTIPLE_APPROVAL;
  }

  const insufficientAdmins = policy.activeAdminCount < 2;
  const disabled =
    isPending || insufficientAdmins || updateApprovalPolicy.isLoading;

  const handleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    nextEnabled: boolean,
  ) => {
    if (disabled) {
      return;
    }

    updateApprovalPolicy.mutate({ enabled: nextEnabled });
  };

  const approvalSwitch = (
    <Styled.SwitchSlot $disabled={disabled}>
      <CustomSwitch
        checked={checked}
        disabled={disabled}
        inputProps={{ 'aria-label': 'Dual Approval' }}
        onChange={handleChange}
      />
    </Styled.SwitchSlot>
  );

  return (
    <Box alignItems="center" display="flex" mb={6}>
      {insufficientAdmins ? (
        <Tooltip
          arrow
          placement="right"
          title={tooltipTitle(INSUFFICIENT_ADMINS_TOOLTIP)}
        >
          <span>{approvalSwitch}</span>
        </Tooltip>
      ) : (
        approvalSwitch
      )}

      <Box ml={1} position="relative">
        <Box alignItems="center" display="flex">
          <Box mr={1}>
            <Typography variant={TypographyVariant.SubHeader1}>
              Dual Approval
            </Typography>
          </Box>

          <Tooltip
            arrow
            placement="right"
            title={
              <Styled.TooltipBody bgcolor="white" p={2} width={280}>
                <Typography variant={TypographyVariant.Paragraph2}>
                  {DUAL_APPROVAL_SUMMARY}
                </Typography>
                <Box mt={1}>
                  <Typography variant={TypographyVariant.Paragraph2}>
                    {RECOVERY_TOOLTIP}
                  </Typography>
                </Box>
              </Styled.TooltipBody>
            }
          >
            <Styled.InfoIcon aria-label={RECOVERY_LABEL} />
          </Tooltip>
        </Box>

        {isPending && (
          <Styled.Status>
            <Typography color="grey3" variant={TypographyVariant.SubLabel}>
              {WAITING_LABEL}
            </Typography>
          </Styled.Status>
        )}
      </Box>
    </Box>
  );
};
